import { FC, useContext } from 'react';

import { useQuery } from '@apollo/client';
import { CreditCardIcon, MoneyIcon, Separator } from '@smartshell/ui-kit';
import cn from 'classnames';

import { AuthContext } from 'auth';
import { LocalizationContext } from 'localization';
import { SettingsContext } from 'settings';
import { formatSum, getCashOrdersDiff } from 'utils';

import s from './Proceeds.module.scss';
import {
  GET_WORKSHIFT_SUMMARY,
  IGetWorkShiftSummaryRequest,
  IGetWorkShiftSummaryResponse,
} from './schema';

interface IWorkerBlockProps {
  workShiftId?: number;
  small?: boolean;
}
export const Proceeds: FC<IWorkerBlockProps> = ({ workShiftId, small }) => {
  const { t } = useContext(LocalizationContext);
  const {
    data: { permissions },
  } = useContext(AuthContext);
  const {
    state: {
      settings: { currency },
    },
  } = useContext(SettingsContext);

  const { data: workshiftMoneyData } = useQuery<
    IGetWorkShiftSummaryResponse,
    IGetWorkShiftSummaryRequest
  >(GET_WORKSHIFT_SUMMARY, {
    fetchPolicy: 'cache-only',
    variables: { id: workShiftId },
    skip: !permissions?.has('work_shift_active'),
  });

  const {
    cash_orders = [],
    cash = 0,
    card = 0,
    cash_on_start = 0,
    total = 0,
  } = workshiftMoneyData?.getWorkShiftPaymentOverviewData ?? {};

  const ordersDiff = getCashOrdersDiff(cash_orders);
  const inCashBox = cash_on_start + cash + ordersDiff;

  const cashPercent = cash && total ? (cash / total) * 100 : 0;
  const cardPercent = card && total ? (card / total) * 100 : 0;

  return (
    <div className={cn(s.container, small && s.small)}>
      <div className={s.left}>
        <div className={s.block}>
          <span className={s.title}>{t('money.proceeds')}</span>
          <span>
            {formatSum(total, currency.sign, { maximumFractionDigits: 2 })}
          </span>
        </div>
        <div className={s.cashbox}>
          <div className={s.block}>
            <span className={s.title}>{t('money.inCashBox')}</span>
            <span>
              {formatSum(inCashBox, currency.sign, {
                maximumFractionDigits: 2,
              })}
            </span>
          </div>
          <div className={s.block}>
            <span className={s.title}>{t('money.atStart')}</span>
            <span>
              {formatSum(cash_on_start, currency.sign, {
                maximumFractionDigits: 2,
              })}
            </span>
          </div>
        </div>
      </div>
      <div className={s.right}>
        <div className={s.block}>
          <span className={s.title}>
            <MoneyIcon size="m" color="green" />
            {t('money.cash')}
          </span>
          <span>
            {formatSum(cash, currency.sign, {
              maximumFractionDigits: 2,
            })}
          </span>
          <span className={cn(s.percent, s.green)}>
            {cashPercent.toLocaleString(undefined, {
              maximumFractionDigits: 2,
            })}
            %
          </span>
        </div>
        <Separator className={s.separator} />
        <div className={s.block}>
          <span className={s.title}>
            <CreditCardIcon size="m" color="link-info" />
            {t('money.card')}
          </span>
          <span>
            {formatSum(card, currency.sign, {
              maximumFractionDigits: 2,
            })}
          </span>
          <span className={cn(s.percent, s.blue)}>
            {cardPercent.toLocaleString(undefined, {
              maximumFractionDigits: 2,
            })}
            %
          </span>
        </div>
      </div>
    </div>
  );
};
